import React, { useCallback } from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';
import { ProgressBar as PropulsionProgressBar } from 'propulsion-rc-v2';
import { truncateLongName } from '../../utils/util';

const ProgressBar = ({ steps, pathName, primaryFirstName, secondaryFirstName, isJoint, showProgressBar }) => {
  const isPrimaryStep = useCallback(() => steps.primarySteps.indexOf(pathName) > -1, [steps, pathName]);
  const isSecondaryStep = useCallback(() => steps.secondarySteps.indexOf(pathName) > -1, [steps, pathName]);

  /**
   * Calculates whether the Primary/Secondary progressBar is done or not.
   * If the currentStep index is more than the index of last step of Primary, we treat primaryProgressBar as done.
   * If the currentStep index is more than the index of last step of Secondary, we treat secondaryProgressBar as done.
   * We show the done 'check' on progressBar only in case of joint flows.
   *
   * @returns {*[]}
   */
  const isDone = useCallback(() => {
    const { allSteps, primarySteps, secondarySteps } = steps; // Steps for secondary progress bar
    const currentStep = pathName; // Current step borrower is at
    const currentStepIndex = allSteps.indexOf(currentStep); // Index of current step in the whole flow (allSteps)
    // Index of last step for primary progressBar. For SSN, we don't have any primarySteps. So, check for length.
    const lastPrimaryStepIndex = primarySteps.length > 1 ? allSteps.indexOf(primarySteps[primarySteps.length - 1]) : '';

    // Incase of SSN_Unhappy, we have only 1 step in the all steps so just default to true.
    const isPrimaryDone =
      currentStepIndex === allSteps.length - 1 || primarySteps.length > 1
        ? currentStepIndex > lastPrimaryStepIndex
        : true;
    let isSecondaryDone = false;
    if (isJoint) {
      const lastSecondaryStepIndex = allSteps.indexOf(secondarySteps[secondarySteps.length - 1]);
      isSecondaryDone = allSteps.length > 1 ? currentStepIndex > lastSecondaryStepIndex : true;
    }

    return [isPrimaryDone, isSecondaryDone];
  }, [steps, pathName, isJoint]);

  const getCurrentPrimaryStep = useCallback(() => {
    if (isPrimaryStep()) return steps.primarySteps.indexOf(pathName) + 1;
    if (isSecondaryStep() || isDone()[0]) return steps.primarySteps.length;
    return 0;
  }, [steps, pathName, isPrimaryStep, isSecondaryStep, isDone]);

  const getCurrentSecondaryStep = useCallback(() => {
    const currentStepIndex = steps.allSteps.indexOf(pathName);
    const firstSecondaryStepIndex = steps.allSteps.indexOf(steps.secondarySteps[0]);
    if (isSecondaryStep()) {
      return steps.secondarySteps.indexOf(pathName) + 1;
    }
    if (currentStepIndex < firstSecondaryStepIndex) {
      return 0;
    }
    return steps.primarySteps.length;
  }, [steps, pathName, isSecondaryStep]);

  const getTitles = useCallback(() => {
    const borrowers = [{ name: truncateLongName(primaryFirstName) || 'You' }];
    if (isJoint) {
      borrowers.push({
        name: truncateLongName(secondaryFirstName) || 'Your co-borrower',
      });
    }
    return borrowers;
  }, [isJoint, primaryFirstName, secondaryFirstName]);

  const renderProgressBar = useCallback(
    (borrower, index) => {
      const progress = [
        // if steps are done, show full progress
        isDone[0] ? 100 : (getCurrentPrimaryStep() / steps.primarySteps.length) * 100,
        isDone[1] ? 100 : (getCurrentSecondaryStep() / steps.secondarySteps.length) * 100,
      ];

      return (
        <div key={index} className={`progress ${isJoint ? 'progress--joint' : ''}`}>
          {isJoint && <span>{borrower.name}</span>}
          <PropulsionProgressBar percentage={progress[index]} />
        </div>
      );
    },
    [isJoint, steps, isDone, getCurrentPrimaryStep, getCurrentSecondaryStep]
  );

  const borrowers = getTitles();
  const progressBarClassNames = classNames({
    'progress-bar': true,
    'progress-bar--hide': !showProgressBar,
  });

  return (
    <div className={progressBarClassNames}>
      <div className="progress__container">
        <div className="progress__wrapper">{borrowers.map(renderProgressBar)}</div>
      </div>
    </div>
  );
};

ProgressBar.propTypes = {
  pathName: propTypes.string.isRequired,
  showProgressBar: propTypes.bool.isRequired,
  steps: propTypes.shape({
    allSteps: propTypes.arrayOf(propTypes.string).isRequired,
    primarySteps: propTypes.arrayOf(propTypes.string).isRequired,
    secondarySteps: propTypes.arrayOf(propTypes.string).isRequired,
  }).isRequired,
  isJoint: propTypes.bool.isRequired,
  primaryFirstName: propTypes.string,
  secondaryFirstName: propTypes.string,
};

ProgressBar.defaultProps = {
  primaryFirstName: '',
  secondaryFirstName: '',
};

export default ProgressBar;

require('/var/lib/jenkins/workspace/borrower-apply-ui_Release_0/src/pages/pi1-1QAAT/components/ProgressBar/ProgressBar.scss');